import { baseApi, PaginatedResponse } from 'sama-shared';
import type { DeliveryIntegration } from '../../types/admin';
import type { GetDeliveryIntegrationsParams } from '../../types/api';
import type { CreateDeliveryIntegrationDto, UpdateDeliveryIntegrationDto } from '../../types/dto';

export const deliveryIntegrationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // 1. Inyectamos los 'params' para que RTK Query arme la URL: ?partnerId=123&page=1
    getDeliveryIntegration: builder.query<
      PaginatedResponse<DeliveryIntegration>,
      GetDeliveryIntegrationsParams
    >({
      query: (params) => ({
        url: '/admin/delivery-integration',
        method: 'GET',
        params,
      }),
      // Proveemos un tag general de LISTA, atado al partnerId si es posible
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'DeliveryIntegration' as const, id })),
              { type: 'DeliveryIntegration', id: `LIST-${arg.partnerId || 'ALL'}` },
            ]
          : [{ type: 'DeliveryIntegration', id: 'LIST-ALL' }],
    }),

    getPartnerIntegratedWithDeliveryProvider: builder.query<
      PaginatedResponse<DeliveryIntegration>,
      GetDeliveryIntegrationsParams
    >({
      query: ({ providerId, partnerId }) => ({
        url: '/admin/delivery-integration/partner/integrated/with-provider',
        method: 'GET',
        params: { providerId, partnerId },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'DeliveryIntegration', id: `LIST-${arg.partnerId}` },
      ],
    }),

    getDeliveryIntegrationById: builder.query<DeliveryIntegration, string>({
      query: (id) => ({
        url: `/admin/delivery-integration/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'DeliveryIntegration', id }],
    }),

    createDeliveryIntegration: builder.mutation<DeliveryIntegration, CreateDeliveryIntegrationDto>({
      query: (body) => ({
        url: '/admin/delivery-integration',
        method: 'POST',
        body,
      }),
      // Invalida solo la lista del partner afectado
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DeliveryIntegration', id: `LIST-${arg.partnerId}` },
      ],
    }),

    updateDeliveryIntegration: builder.mutation<
      DeliveryIntegration,
      { id: string; body: UpdateDeliveryIntegrationDto; partnerId: string }
    >({
      query: ({ id, body }) => ({
        url: `/admin/delivery-integration/${id}`,
        method: 'PATCH',
        body,
      }),
      // Invalida el registro específico y la lista del partner
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DeliveryIntegration', id: arg.id },
        { type: 'DeliveryIntegration', id: `LIST-${arg.partnerId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDeliveryIntegrationQuery,
  useGetPartnerIntegratedWithDeliveryProviderQuery,
  useGetDeliveryIntegrationByIdQuery,
  useCreateDeliveryIntegrationMutation,
  useUpdateDeliveryIntegrationMutation,
} = deliveryIntegrationApi;
